import { documentTemplatesQuery } from "@common/api/document-templates"
import type { DocumentTemplatePublic } from "@common/client"
import { GridTable } from "@common/components/ui/grid-table"
import { formatShortDate } from "@common/lib/date-utils"
import { cn } from "@common/lib/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  SettingsTableCell,
  SettingsTableHead,
  SettingsTableHeader,
  SettingsTableRow,
} from "@/routes/_layout/settings/_panel/-components/settings-table"
import { KIND_DESCRIPTIONS, KIND_LABELS } from "./-lib/kinds"

const COLUMNS = "grid-cols-[minmax(10rem,1fr)_minmax(0,2fr)_7rem_8rem]"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/templates/",
)({
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(documentTemplatesQuery()),
  component: TemplatesPage,
})

function TemplatesPage(): React.ReactElement {
  const { data: templates } = useSuspenseQuery(documentTemplatesQuery())

  return (
    <div className="[--filter-bar-height:0px]">
      <GridTable className={COLUMNS}>
        <SettingsTableHeader>
          <SettingsTableHead className="pl-6">Template</SettingsTableHead>
          <SettingsTableHead>Used for</SettingsTableHead>
          <SettingsTableHead>Source</SettingsTableHead>
          <SettingsTableHead className="pr-6">Updated</SettingsTableHead>
        </SettingsTableHeader>
        {templates.map((template) => (
          <TemplateRow key={template.kind} template={template} />
        ))}
      </GridTable>
    </div>
  )
}

function TemplateRow({
  template,
}: {
  template: DocumentTemplatePublic
}): React.ReactElement {
  return (
    <SettingsTableRow asChild>
      <Link
        to="/settings/workspace/templates/$kind"
        params={{ kind: template.kind }}
        className="text-foreground"
      >
        <TextCell className="pl-6">{KIND_LABELS[template.kind]}</TextCell>
        <TextCell className="text-muted-foreground">
          {KIND_DESCRIPTIONS[template.kind]}
        </TextCell>
        <TextCell>{template.is_default ? "Default" : "Custom"}</TextCell>
        <TextCell className="pr-6 text-muted-foreground">
          {template.updated_at ? formatShortDate(template.updated_at) : ""}
        </TextCell>
      </Link>
    </SettingsTableRow>
  )
}

function TextCell({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <SettingsTableCell className={cn("text-body-regular", className)}>
      <span className="truncate">{children}</span>
    </SettingsTableCell>
  )
}
